import { Option } from '@inrev/core';
import { Decimal } from 'decimal.js-light';
import { match } from 'ts-pattern';
import * as z from 'zod';
import { fileExtensionSchema, fileTypeSchema, fileClassificationStatusSchema, fileTypeDef, fileExtensionDef, FileExtension, FileType } from '../enums.mjs';
import { requiredOr } from '../utils/required-error.util.mjs';
import { applyCurrencyBrand } from './currency/currency.mjs';
import { rateSchema, fileId, attachmentId, suretyBondFormTemplateId } from './opaque.mjs';

function isPlainObject(value) {
    if (Object.prototype.toString.call(value) !== "[object Object]")
        return false;
    const proto = Object.getPrototypeOf(value);
    return proto === null || proto === Object.getPrototypeOf({});
}
const buildCoercedNumberSchema = (numberSchema = z.number()) => z
    .any()
    .transform((val) => {
    if (val === "")
        return undefined;
    if (val === null || val === undefined)
        return undefined;
    return Number(val);
})
    .pipe(numberSchema);
const buildCoercedOpaqueNumberSchema = (opaqueSchema) => buildCoercedNumberSchema(opaqueSchema).brand();
const safeNumberSchema = z.coerce
    .number({ error: requiredOr("Not a number") })
    .max(2147483647)
    .min(-2147483648);
const MAX_INPUT_VALUE = 99999999999999.99;
const _currencySchema = z
    .number({ error: (issue) => (issue.input === undefined ? "Required" : "Not a valid number") })
    .min(-MAX_INPUT_VALUE, { error: "Too small" })
    .max(MAX_INPUT_VALUE, { error: "Too large" });
const currencySchema = buildCoercedOpaqueNumberSchema(applyCurrencyBrand(_currencySchema));
const nonNegativeCurrencySchema = buildCoercedOpaqueNumberSchema(applyCurrencyBrand(_currencySchema.min(0)));
const positiveCurrencySchema = buildCoercedOpaqueNumberSchema(applyCurrencyBrand(_currencySchema.positive({ error: "Amount must be greater than 0" })));
const toISODate = (dt) => dt.toUTC().toISODate();
const toISODateTime = (dt) => dt.toUTC().toISO();
const contractSuretyTypeToString = (contractSuretyType) => {
    return match(contractSuretyType)
        .with("bid", () => "Bid")
        .with("bid_to_final", () => "Bid To Final")
        .with("final", () => "Final")
        .exhaustive();
};
const entityTypeToDisplayString = (entityType) => {
    return match(entityType)
        .with("account", () => "Account")
        .with("bond", () => "Bond")
        .with("quote", () => "Bond Request")
        .with("actionItem", () => "Action Item")
        .exhaustive();
};
const baseUploadedFile = z.object({
    id: z.string(),
    name: z.string(),
    extension: fileExtensionSchema,
    types: z.array(fileTypeSchema).optional(),
});
const uploadedFileWithoutId = baseUploadedFile.omit({ id: true });
const uploadedFileSchema = (allowedFileTypes, allowedFileExtensions) => z.object({
    id: fileId,
    name: z.string(),
    extension: z.enum((allowedFileExtensions ?? fileExtensionDef)),
    types: z
        .array(z.enum((allowedFileTypes ?? fileTypeDef)))
        .default([]),
    typesConfirmedByAdmin: z.boolean().default(false),
    classificationStatus: fileClassificationStatusSchema.optional(),
});
const attachmentSchema = (allowedFileTypes, allowedFileExtensions) => z.object({
    id: attachmentId,
    file: uploadedFileSchema(allowedFileTypes, allowedFileExtensions),
    createdAtMs: z.number(),
});
const uploadedFileCollectionSchema = (requiredFileTypes, allowedFileTypes, allowedFileExtensions) => z.array(uploadedFileSchema(allowedFileTypes, allowedFileExtensions)).refine((files) => {
    if (requiredFileTypes &&
        !requiredFileTypes.every((req) => files.some((file) => file.types?.includes(req) && file.types.length > 0)))
        return false;
    if (allowedFileTypes &&
        !files.every((file) => file.types?.every((type) => allowedFileTypes.includes(type))))
        return false;
    return true;
});
const stringOrEmptyString = z.union([z.string(), z.literal("")]);
const boolOrEmptyString = z.union([z.boolean(), z.literal("")]);
const maybePartialSchema = (partial, validationSchema) => {
    if (partial) {
        return validationSchema.nullish();
    }
    return validationSchema;
};
const pruneKeys = (targetObj, referenceObj) => {
    Object.keys(targetObj).forEach((key) => {
        if (referenceObj[key] === undefined) {
            delete targetObj[key];
        }
    });
    return targetObj;
};
const bondFormSubmissionSchema = (allowedTypes) => {
    return z.union([
        z.object({
            templateId: suretyBondFormTemplateId,
        }),
        z.object({
            upload: z.object({
                type: z.enum(allowedTypes),
                files: z
                    .array(uploadedFileSchema([FileType.blank_bond_form], [
                    FileExtension.pdf,
                    FileExtension.png,
                    FileExtension.gif,
                    FileExtension.jpg,
                    FileExtension.jpeg,
                    FileExtension.docx,
                    FileExtension.doc,
                ]))
                    .min(1),
            }),
        }),
    ]);
};
const draftBondFormSchema = (allowedTypes) => {
    return z.object({
        templateId: stringOrEmptyString,
        upload: z.object({
            type: z.union([z.enum(allowedTypes), z.literal("")]),
            files: z.array(uploadedFileSchema([FileType.blank_bond_form], [
                FileExtension.pdf,
                FileExtension.png,
                FileExtension.gif,
                FileExtension.jpg,
                FileExtension.jpeg,
                FileExtension.docx,
                FileExtension.doc,
            ])),
        }),
    });
};
const stripEmpty = (value) => {
    if (value === "")
        return undefined;
    return value;
};
const nullEmpty = (value) => {
    if (value === "")
        return null;
    return value;
};
const numberToStringSchema = (min) => z
    .number()
    .pipe(z.transform((val) => String(val)))
    .pipe(z.string().min(min ?? 0, `${min} Minimum`));
const stripEmptyDeep = (value, returnEmptyObject = false, returnEmptyArray = true) => {
    if (value === "")
        return undefined;
    if (Array.isArray(value)) {
        const stripped = value.map((item) => stripEmptyDeep(item, returnEmptyObject, returnEmptyArray));
        if (stripped.length || returnEmptyArray) {
            return stripped;
        }
        return undefined;
    }
    if (isPlainObject(value)) {
        const newObj = {};
        for (const key in value) {
            const newValue = stripEmptyDeep(value[key], returnEmptyObject, returnEmptyArray);
            if (newValue !== undefined) {
                newObj[key] = newValue;
            }
        }
        if (Object.keys(newObj).length || returnEmptyObject) {
            return newObj;
        }
        return undefined;
    }
    return value;
};
const nullEmptyDeep = (value, returnEmptyObject = false) => {
    if (value === "")
        return null;
    if (Array.isArray(value))
        return value.map((item) => nullEmptyDeep(item, returnEmptyObject));
    if (isPlainObject(value)) {
        const newObj = {};
        for (const key in value) {
            const newValue = nullEmptyDeep(value[key], returnEmptyObject);
            if (newValue !== undefined) {
                newObj[key] = newValue;
            }
        }
        if (Object.keys(newObj).length || returnEmptyObject) {
            return newObj;
        }
        return null;
    }
    return value;
};
const isSubsetOf = (a, b) => {
    if (a.size > b.size)
        return false;
    for (const val of a) {
        if (!b.has(val))
            return false;
    }
    return true;
};
const isSetOverlap = (a, b) => {
    let subject;
    let target;
    if (a.size > b.size) {
        subject = b;
        target = a;
    }
    else {
        subject = a;
        target = b;
    }
    for (const val of subject) {
        if (target.has(val))
            return true;
    }
    return false;
};
const initializeExposure = () => ({
    quotes: 0,
    bonds: 0,
    total: 0,
});
const exposureSchema = z.object({
    quotes: z.number(),
    bonds: z.number(),
    total: z.number(),
});
const suretyAccountBondingLineSchema = z.object({
    rate: rateSchema,
    singleLimit: nonNegativeCurrencySchema,
    aggregateLimit: nonNegativeCurrencySchema,
    isPublished: z.boolean(),
});
const coerceBondingLinePublish = (bondingLine) => bondingLine.isPublished && bondingLine.singleLimit <= 0
    ? { ...bondingLine, isPublished: false }
    : bondingLine;
const publishableSuretyAccountBondingLineSchema = suretyAccountBondingLineSchema.transform(coerceBondingLinePublish);
const numberFormatter = new Intl.NumberFormat("en");
const formatAddress = (address) => {
    if (Option.isNone(address))
        return "";
    if (typeof address === "string")
        return address;
    let addressString = "";
    if (address.street?.length)
        addressString += `${address.street} `;
    if (address.city?.length)
        addressString += `${address.city}, `;
    if (address.state?.length)
        addressString += `${address.state} `;
    if (address.zip?.length)
        addressString += address.zip;
    return addressString;
};
function formatToDollar(value, options) {
    if (value === undefined || value === null || value === "") {
        return value;
    }
    const round = typeof options === "boolean" ? options : (options?.round ?? false);
    const accounting = typeof options === "boolean" ? false : (options?.accounting ?? false);
    const abbreviate = typeof options === "boolean" ? false : (options?.abbreviate ?? false);
    const omitSymbol = typeof options === "boolean" ? false : (options?.omitSymbol ?? false);
    const num = new Decimal(round ? Math.round(Number(value)) : value).toDecimalPlaces(2).toNumber();
    let formatted = "";
    if (abbreviate) {
        if (Math.abs(num) >= 1_000_000_000_000) {
            formatted = `${new Decimal(Math.abs(num) / 1_000_000_000_000).toDecimalPlaces(2).toNumber()}T`;
        }
        else if (Math.abs(num) >= 1_000_000_000) {
            formatted = `${new Decimal(Math.abs(num) / 1_000_000_000).toDecimalPlaces(2).toNumber()}B`;
        }
        else if (Math.abs(num) >= 1_000_000) {
            formatted = `${new Decimal(Math.abs(num) / 1_000_000).toDecimalPlaces(1).toNumber()}M`;
        }
        else if (Math.abs(num) >= 1_000) {
            formatted = `${new Decimal(Math.abs(num) / 1_000).toDecimalPlaces(0).toNumber()}K`;
        }
        else {
            formatted = `${numberFormatter.format(Math.abs(num))}`;
        }
    }
    else {
        formatted = `${numberFormatter.format(Math.abs(num))}`;
    }
    if (!omitSymbol) {
        formatted = `$${formatted}`;
    }
    if (accounting && num < 0) {
        return `(${formatted})`;
    }
    return num < 0 ? `-${formatted}` : formatted;
}
function formatToPercent(value, precision = 2, defaultValue) {
    if (value === undefined || value === null || value === "") {
        return defaultValue ?? "";
    }
    return `${new Decimal(value).times(100).toDecimalPlaces(precision).toNumber()}%`;
}
const formatYesNo = (value) => {
    if (value === undefined || value === null || value === "")
        return value;
    return value ? "Yes" : "No";
};
const keys = (obj) => {
    return Object.keys(obj);
};
const bondFormFileSchema = z.object({
    id: fileId,
    name: z.string(),
    extension: z.enum(["pdf", "docx", "doc", "png", "jpg", "jpeg", "gif"]),
    types: z.array(z.literal("blank_bond_form")).default([]),
    typesConfirmedByAdmin: z.boolean().default(false),
});
const financialStatementFileSchema = z.object({
    id: fileId,
    name: z.string(),
    extension: fileExtensionSchema,
    types: z.array(fileTypeSchema).default([]),
    typesConfirmedByAdmin: z.boolean().default(false),
});
const wipStatementFileSchema = z.object({
    id: fileId,
    name: z.string(),
    extension: fileExtensionSchema,
    types: z.array(z.literal("wip")).default([]),
    typesConfirmedByAdmin: z.boolean().default(false),
});
const attachmentFileSchema = z.object({
    id: fileId,
    name: z.string(),
    extension: fileExtensionSchema,
    types: z.array(fileTypeSchema).default([]),
    typesConfirmedByAdmin: z.boolean().default(false),
});
const bondFormFileCollectionSchema = z.array(bondFormFileSchema);
const financialStatementFileCollectionSchema = z.array(financialStatementFileSchema);
const wipStatementFileCollectionSchema = z.array(wipStatementFileSchema);
const attachmentFileCollectionSchema = z.array(attachmentFileSchema);
const preProcessFileTypeSchema = (schema) => {
    return z.object({
        types: z.preprocess((val) => (val ? val.split(", ") : []), z.array(schema)),
    });
};
const asyncSleep = (milliseconds) => {
    return new Promise((res, _rej) => {
        setTimeout(() => {
            res();
        }, milliseconds);
    });
};
const fetchWithExponentialBackoff = async (input, init, retries = 5, initialDelay = 1000) => {
    let lastError = null;
    for (let i = 0; i < retries; i++) {
        try {
            const response = await fetch(input, init);
            if (response.ok) {
                return response;
            }
            if (response.status >= 400 && response.status < 500) {
                throw new Error(`Client error: ${response.status} ${response.statusText}`);
            }
            lastError = new Error(`Server error: ${response.status} ${response.statusText}`);
        }
        catch (error) {
            lastError = error instanceof Error ? error : new Error(String(error));
        }
        if (i < retries - 1) {
            const delay = initialDelay * Math.pow(2, i);
            const jitter = Math.random() * 0.1 * delay;
            await asyncSleep(delay + jitter);
        }
    }
    return Promise.reject(lastError ?? new Error(`Failed to fetch after ${retries} attempts`));
};
const capitalize = (str) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
};
const pluralize = (count, singular, plural) => {
    if (count === undefined || count === null)
        return "";
    return `${count} ${count === 1 ? singular : plural}`;
};
const formatFileNames = (files) => {
    if (!files || files.length === 0)
        return undefined;
    return files.map((file) => `${file.name}.${file.extension}`).join(", ");
};

export { MAX_INPUT_VALUE, asyncSleep, attachmentFileCollectionSchema, attachmentFileSchema, attachmentSchema, baseUploadedFile, bondFormFileCollectionSchema, bondFormFileSchema, bondFormSubmissionSchema, boolOrEmptyString, capitalize, coerceBondingLinePublish, contractSuretyTypeToString, currencySchema, draftBondFormSchema, entityTypeToDisplayString, exposureSchema, fetchWithExponentialBackoff, financialStatementFileCollectionSchema, financialStatementFileSchema, formatAddress, formatFileNames, formatToDollar, formatToPercent, formatYesNo, initializeExposure, isSetOverlap, isSubsetOf, keys, maybePartialSchema, nonNegativeCurrencySchema, nullEmpty, nullEmptyDeep, numberToStringSchema, pluralize, positiveCurrencySchema, preProcessFileTypeSchema, pruneKeys, publishableSuretyAccountBondingLineSchema, safeNumberSchema, stringOrEmptyString, stripEmpty, stripEmptyDeep, suretyAccountBondingLineSchema, toISODate, toISODateTime, uploadedFileCollectionSchema, uploadedFileSchema, uploadedFileWithoutId, wipStatementFileCollectionSchema, wipStatementFileSchema };
